import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import {
  addQuestion,
  createBlankTemplate,
  deleteQuestion,
  deleteTemplate,
  getTemplate,
  listTemplateQuestion,
  listTemplates,
  renameSection,
  updateOption,
  updateQuestion,
  updateTemplate,
} from '@/gen/questionnaire/v1/template_service-TemplateService_connectquery'
import { TemplateService } from '@/gen/questionnaire/v1/template_service_pb'

import { defaultTransport } from '@/lib/connect/connect-transport'

// --- Query Key Factories ---

export const getGetTemplateQueryKey = (templateId: string) =>
  createConnectQueryKey({
    schema: TemplateService.method.getTemplate,
    input: { templateId },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getListTemplatesQueryKey = () =>
  createConnectQueryKey({
    schema: TemplateService.method.listTemplates,
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getListTemplateQuestionQueryKey = (templateId: string) =>
  createConnectQueryKey({
    schema: TemplateService.method.listTemplateQuestion,
    input: { templateId },
    transport: defaultTransport,
    cardinality: 'finite',
  })

// --- Hooks ---

export const useGetQuestionnaireTemplate = ({ templateId }: { templateId: string }) => {
  const queryState = useQuery(getTemplate, { templateId })
  return {
    ...queryState,
    data: queryState.data?.template,
  }
}

export const useListQuestionnaireTemplates = () => {
  const queryState = useQuery(listTemplates, {})
  return {
    ...queryState,
    data: queryState.data?.templates || [],
  }
}

export const useListTemplateQuestion = (templateId?: string) => {
  const queryState = useQuery(
    listTemplateQuestion,
    { templateId: templateId || '' },
    {
      enabled: !!templateId,
    },
  )

  return {
    ...queryState,
    data: queryState.data?.questions.sort((a, b) => a.displayOrder - b.displayOrder) || [],
  }
}

export const useRenameSection = () => {
  const queryClient = useQueryClient()

  return useMutation(renameSection, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getListTemplateQuestionQueryKey(variables.templateId || ''),
      })
      queryClient.invalidateQueries({
        queryKey: getGetTemplateQueryKey(variables.templateId || ''),
      })
      queryClient.invalidateQueries({ queryKey: getListTemplatesQueryKey() })
    },
  })
}

export const useUpdateTemplateQuestion = (templateId: string) => {
  const queryClient = useQueryClient()

  return useMutation(updateQuestion, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListTemplateQuestionQueryKey(templateId) })
      queryClient.invalidateQueries({ queryKey: getGetTemplateQueryKey(templateId) })
      queryClient.invalidateQueries({ queryKey: getListTemplatesQueryKey() })
    },
  })
}

export const useUpdateTemplateOption = (templateId: string, _questionId: number) => {
  const queryClient = useQueryClient()
  return useMutation(updateOption, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getListTemplateQuestionQueryKey(templateId),
      })
      queryClient.invalidateQueries({
        queryKey: getGetTemplateQueryKey(templateId),
      })
    },
  })
}

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient()
  const listTemplatesKey = getListTemplatesQueryKey()
  return useMutation(updateTemplate, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getGetTemplateQueryKey(variables.templateId || ''),
      })
      queryClient.invalidateQueries({ queryKey: listTemplatesKey })
    },
  })
}

export const useDeleteTemplateQuestion = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteQuestion, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getListTemplateQuestionQueryKey(variables.templateId || ''),
      })
      queryClient.invalidateQueries({
        queryKey: getGetTemplateQueryKey(variables.templateId || ''),
      })
    },
  })
}

export const useAddTemplateQuestion = (templateId: string) => {
  const queryClient = useQueryClient()

  const addQuestionMutation = useMutation(addQuestion, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListTemplateQuestionQueryKey(templateId) })
      queryClient.invalidateQueries({ queryKey: getGetTemplateQueryKey(templateId) })
      queryClient.invalidateQueries({ queryKey: getListTemplatesQueryKey() })
    },
  })

  return {
    mutate: addQuestionMutation.mutate,
    isPending: addQuestionMutation.isPending,
    originalMutation: addQuestionMutation,
  }
}

export const useOptimisticUpdateTemplateQuestion = (templateId: string) => {
  const queryClient = useQueryClient()

  return useMutation(updateQuestion, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListTemplateQuestionQueryKey(templateId) })
      queryClient.invalidateQueries({ queryKey: getGetTemplateQueryKey(templateId) })
    },
  })
}

export const useCreateBlankTemplate = () => {
  const queryClient = useQueryClient()
  const listTemplatesKey = getListTemplatesQueryKey()

  const mutation = useMutation(createBlankTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTemplatesKey })
    },
  })

  return mutation
}

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient()
  const listTemplatesKey = getListTemplatesQueryKey()
  return useMutation(deleteTemplate, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTemplatesKey })
    },
  })
}
